import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { colors } from '../theme';

interface ServiceCard {
  logo: string;
  title: string;
  items: string[];
  link?: string;
  isExternal?: boolean;
}

const ProcessControl: React.FC = () => {
  const serviceCards: ServiceCard[] = [
    {
      logo: '/Process-Control-logo.jpg',
      title: 'Panel Shop',
      items: ['Fabrication', 'VFD commissioning', 'Retrofit', 'Onsite services'],
    },
    {
      logo: '/Sauereisen-logo.jpg',
      title: 'Corrosion Protection & Ceramic Assembly',
      items: [
        'Corrosion and Inflow & Infiltration',
        'Collection Systems and Treatment Plants',
        'Restoration and New Construction',
        'Specialized Corrosion-Resistant Lining',
        'Substrate Repair Materials and Filler Compounds',
        'Preventing Inflow and Infiltration',
      ],
      link: 'https://www.sauereisen.com/',
      isExternal: true,
    },
    {
      logo: '/Snyder-logo.jpg',
      title: 'Storage Solutions Experts',
      items: [
        'Single Wall Tanks',
        'Double Wall Containment',
        'Horizontal Tanks',
        'Chemical Feed Stations',
        'Chemical Day Tanks',
        'IBC Totes',
      ],
      link: 'https://www.snydernet.com/',
      isExternal: true,
    },
    {
      logo: '/Plas-Tanks-logo.jpg',
      title: 'Composite Vessel Manufacturer',
      items: [
        'Fiberglass Reinforced Plastic (FRP) Brinemakers',
        'FRP Storage Tanks',
        'FRP Mixing Tanks',
        'FRP Process Vessels',
      ],
      link: 'https://www.plastanks.com/',
      isExternal: true,
    },
    {
      logo: '/3nine-logo.jpg',
      title: 'Oil Mist Eliminators',
      items: [
        'CNC machine mist eliminators',
        'Oil skimmers',
        'Oil residue vacuum recycler',
        'Low maintenance',
        'Only one filter',
        'Self cleaning',
      ],
      link: 'https://www.3nine.us/',
      isExternal: true,
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: `linear-gradient(135deg, transparent 0%, rgba(245, 166, 35, 0.1) 100%)`,
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2, mb: 1, display: 'block' }}
            >
              Our Division
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                fontWeight: 700,
                mb: 2,
              }}
            >
              Process Control
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '1.1rem',
                maxWidth: 500,
              }}
            >
              Custom electrical control panel design and fabrication
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Accent Line */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)` }} />

      {/* Quote Button Section */}
      <Box
        sx={{
          py: 4,
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
            >
              Need a Quote Right Now?
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Electrical Control Panel Design Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
              >
                What We Do
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: colors.primary,
                  mb: 1,
                  mt: 1,
                }}
              >
                Electrical Control Panel Design:
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  color: colors.secondary,
                  mb: 4,
                  fontWeight: 700,
                }}
              >
                Process Control Panel Shop
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  mb: 3,
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                }}
              >
                Specializing in variable frequency drive (VFD) repackaging, pump control panels, fan-wall panels, starter panels, and all types of process-equipment sales.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  mb: 4,
                }}
              >
                Our onsite retrofits, upgrades, services, chemical-storage solutions, and corrosion-protection services benefit customers from a variety of industries.
              </Typography>
              <Button
                component={Link}
                to="/services"
                variant="outlined"
                color="secondary"
                endIcon={<ArrowForwardIcon />}
              >
                View All Services
              </Button>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 24,
                    left: 24,
                    right: -24,
                    bottom: -24,
                    border: `3px solid ${colors.primary}`,
                    borderRadius: 4,
                    zIndex: -1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/Electrical-Control-Panels.jpeg"
                  alt="Electrical Control Panels"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Service Cards Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
            >
              Our Partners
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: colors.secondary,
                mt: 1,
              }}
            >
              Solutions We Provide
            </Typography>
          </Box>

          {/* First Row - 3 Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {serviceCards.slice(0, 3).map((card, index) => (
              <Grid item xs={12} md={4} key={index}>
                <ServiceCardComponent card={card} />
              </Grid>
            ))}
          </Grid>

          {/* Second Row - 2 Cards */}
          <Grid container spacing={3} justifyContent="center">
            {serviceCards.slice(3, 5).map((card, index) => (
              <Grid item xs={12} md={4} key={index}>
                <ServiceCardComponent card={card} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                color: '#FFFFFF',
                mb: 3,
              }}
            >
              Ready to Start Your Project?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.1rem',
                mb: 4,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              Let our team of experts help you design and build your next control panel or find the right equipment for your needs.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#FFFFFF',
                color: colors.primary,
                px: 5,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 8px 30px rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              Contact Us Today
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

// Service Card Component
const ServiceCardComponent: React.FC<{ card: ServiceCard }> = ({ card }) => {
  const CardInner = (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: colors.border,
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'visible',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          borderRadius: '12px 12px 0 0',
        },
        '&:hover': {
          boxShadow: `0 16px 40px rgba(245, 166, 35, 0.15)`,
          transform: 'translateY(-6px)',
          borderColor: colors.primary,
          '& .external-icon': {
            opacity: 1,
          },
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ position: 'relative' }}>
          <Box
            component="img"
            src={card.logo}
            alt={card.title}
            sx={{
              width: '100%',
              maxWidth: 180,
              height: 'auto',
              mb: 2,
              display: 'block',
              mx: 'auto',
            }}
          />
          {card.isExternal && (
            <OpenInNewIcon
              className="external-icon"
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                fontSize: 18,
                color: colors.primary,
                opacity: 0,
                transition: 'opacity 0.3s ease',
              }}
            />
          )}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontSize: '1.1rem',
            color: colors.secondary,
            fontWeight: 700,
            mb: 2,
          }}
        >
          {card.title}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {card.items.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: colors.primary,
                  mt: '0.5em',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: colors.textSecondary,
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                }}
              >
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  if (card.link && card.isExternal) {
    return (
      <Box
        component="a"
        href={card.link}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          textDecoration: 'none',
          display: 'block',
          height: '100%',
        }}
      >
        {CardInner}
      </Box>
    );
  }

  if (card.link) {
    return (
      <Box
        component={Link}
        to={card.link}
        sx={{
          textDecoration: 'none',
          display: 'block',
          height: '100%',
        }}
      >
        {CardInner}
      </Box>
    );
  }

  return CardInner;
};

export default ProcessControl;
